import { BillingCode, BillingCodeBilledAmount, CodeSets } from '../types/BillingCode.type';
import { FormErrorsStrict } from '../types/ErrorResponse.type';
import { STRING_BOOLEAN_HASH } from './globals';
import { ChiroUpBaseCommon } from './stringConstants';

export type TabParamsType = {
  name: string;
  href: string;
  active: boolean;
  routePath?: string;
};

export type BillingCodePayorType = {
  ID?: number;
  billingCodeID: number;
  payorID: number;
  allowedAmount: string | number;
  modifier1: string;
  modifier2: string;
  modifier3: string;
  modifier4: string;
};

export type PayorListType = {
  [key: string]: any;
  ID?: number;
  billingCodeID?: number;
  payorIntPk?: number;
  payorId?: string;
  legalName?: string;
  allowedAmount?: string | number;
  modifier1?: string;
  modifier2?: string;
  modifier3?: string;
  modifier4?: string;
  payors?: BillingCodePayorType[];
};

export const FeaturePath = '/settings/database';

/**
 * It seems that the same code will need to be duplicated lots of places if
 * something like this isn't done. I know it is just an 'any', but we could
 * throw an error if the field does not exist.
 */
export const FieldValidators = {
  isValidPayorModifier: (modKey: string) => {
    return {
      function: {
        value: (values: any) => {
          let resp: string | false = false;
          const [field, ord] = (modKey || '').split(/[[\]]/) || ['bogus', '-1'],
            index = Number.parseInt(ord),
            refs = values?.defaultModifiers || [];

          if (index !== -1) {
            const val = values?.[field]?.[index] || '';

            if (val) {
              resp =
                refs.indexOf(val) === -1
                  ? false
                  : `Payor-specific modifier '${val}' duplicates a default modifier.`;
            }
          }

          return resp;
        },
      },
    };
  },
  phone: () => {
    return {
      function: {
        value: (values: any) => {
          const { phone } = values;

          if (phone?.[1] === '1' && phone?.split(' ')?.[0]?.length === 6 && phone?.length < 13) {
            return 'Phone number must be 7 digits.';
          } else if (phone?.[1] === '1' && phone?.length < 13) {
            return 'Phone number must be 10 digits.';
          }

          return false;
        },
      },
    };
  },
  isRequired: (text: string) => {
    return { required: { message: `${text} is required.` } };
  },
  isCurrency: (key: string, text: string, required: string) => {
    return {
      function: {
        value: (values: any) => {
          const amount = values?.[key] ? `${values?.[key]}` : 0;
          if (required !== 'required' && !amount) return false;
          const [t1, t2] = (amount || '').split(/\./),
            num1 = t1?.replace(/\D/g, ''),
            num2 = t2?.replace(/\D/g, '');

          if (t1 === num1 && t2 === num2 && (t2 || '').length === 2) {
            return false;
          }

          return `${text} should be in the format 0.00 with no commas or dollar sign.`;
        },
      },
    };
  },
  stringMinMax: (key: string, text: string, min: number, max: number) => {
    return {
      function: {
        value: (values: any) => {
          const str = values?.[key];
          min = min || 0;

          if (!str && !min) return false; // if no min, then no error.

          if (str) {
            if (str.length >= min && str.length <= max) return false;
          }

          return `${text} needs to be between ${min} and ${max} characters in length.`;
        },
      },
    };
  },
};

export enum InstanceAccessLevel {
  none = '',
  read = 'r',
  readWrite = 'rw',
}

export enum InstanceTableNames {
  payors = 'Payor',
  billingCodes = 'BillingCode',
  noShowCancelFees = 'NoShowCancelFees',
  billingAdjustments = 'BillingAdjustments',
  billingProfiles = 'BillingProfiles',
  caseTypes = 'ClinicCaseType',
  packages = 'Packages',
}

export const pathToInstance = {
  payors: 'payors',
  'billing-codes': 'billingCodes',
  'no-show-cancel-fees': 'noShowCancelFees',
  'billing-adjustments': 'billingAdjustments',
  'billing-profiles': 'billingProfiles',
  'case-types': 'caseTypes',
  packages: 'packages',
} as const;

export enum InstanceKeys {
  payors = 'payors',
  billingCodes = 'billing-codes',
  noShowCancelFees = 'no-show-cancel-fees',
  billingAdjustments = 'billing-adjustments',
  billingProfiles = 'billing-profiles',
  caseTypes = 'case-types',
  packages = 'packages',
}

export enum InstanceTitles {
  payors = 'Payors',
  billingCodes = 'Billing Codes',
  noShowCancelFees = 'No-Show & Cancel Fees',
  billingAdjustments = 'Billing Discounts',
  billingProfiles = 'Billing Profiles',
  caseTypes = 'Custom Case Types',
  packages = 'Packages',
}

/**
 * This builds the href for a tab.
 *
 * @param instance
 * @returns
 */
export const instanceHref = (instance: InstanceKeys) => `${FeaturePath}/${instance}`;

/**
 * Convenience method. Normally the same as the href in a tab,
 * but may not always be the case.
 *
 * @param instance
 * @returns
 */
export const instancePathname = (instance: InstanceKeys) => instanceHref(instance);

export const InsurancePrograms = [
  { value: 'auto', text: 'Automobile Medical', shortText: 'Automobile Medical' },
  { value: 'bcbs', text: 'Blue Cross / Blue Shield', shortText: 'BCBS' },
  { value: 'champus', text: 'Champus', shortText: 'Champus' },
  // { value: 'champva', text: 'Champva' },
  { value: 'commercial', text: 'Commercial Insurance Co.', shortText: 'Commercial' },
  { value: 'dmo', text: 'Dental Maintenance Organization', shortText: 'DMO' },
  { value: 'disability', text: 'Disability', shortText: 'Disability' },
  { value: 'epo', text: 'Exclusive Provider Organization (EPO)', shortText: 'EPO' },
  // { value: 'feca', text: 'FECA' },
  { value: 'fep', text: 'Federal Employees Program', shortText: 'FEP' },
  // { value: 'geha', text: 'GEHA' },
  // { value: 'ghp', text: 'Group Health Plan (GHP)' },
  { value: 'hmo', text: 'Health Maintenance Organization', shortText: 'HMO' },
  {
    value: 'hmomedicare',
    text: 'Health Maintenance Organization (HMO) Medicare Risk',
    shortText: 'HMO Medicare Risk',
  },
  // { value: 'hdhc', text: 'High Deductible Health Plan (HDHC)' },
  { value: 'ii', text: 'Indemnity Insurance', shortText: 'Indemnity' },
  { value: 'liability', text: 'Liability Medical', shortText: 'Liability Medical' },
  { value: 'mcaid', text: 'Medicaid', shortText: 'Medicaid' },
  { value: 'mcarea', text: 'Medicare Part A', shortText: 'Medicare Part A' },
  { value: 'mcareb', text: 'Medicare Part B', shortText: 'Medicare Part B' },
  {
    value: 'unknown',
    text: 'Mutually Defined or Unknown',
    shortText: 'Mutually Defined or Unknown',
  },
  // { value: 'mcarehmo', text: 'Medicare Risk HMO' },
  { value: 'pos', text: 'Point of Service (POS)', shortText: 'POS' },
  { value: 'ppo', text: 'Preferred Provider Organizations (PPO)', shortText: 'PPO' },
  { value: 'titlev', text: 'Title V', shortText: 'Title V' },
  // { value: 'tricare', text: 'Tricare' },
  { value: 'va', text: 'Veteran Affairs Plan', shortText: 'VA' },
  { value: 'wc', text: 'Workers Compensation', shortText: 'WC' },
  { value: 'otherfed', text: 'Other Federal Program', shortText: 'Other Federal Program' },
  { value: 'other', text: 'Other Non-Federal Program', shortText: 'Other Non-Federal Program' },
];

export const InsuranceProgramEdiCode = {
  other: '11',
  ppo: '12',
  pos: '13',
  epo: '14',
  ii: '15',
  hmomedicare: '16',
  dmo: '17',
  auto: 'AM',
  bcbs: 'BL',
  champus: 'CH',
  commercial: 'CI',
  disability: 'DS',
  fep: 'FI',
  hmo: 'HM',
  liability: 'LM',
  mcarea: 'MA',
  mcareb: 'MB',
  mcaid: 'MC',
  otherfed: 'OF',
  titlev: 'TV',
  va: 'VA',
  wc: 'WC',
  _unknown: 'ZZ',
};

export const itemToOptionText = (instanceKey: string, item: any) => {
  const valueNumber = Number(item.value);
  const valueDisplay = valueNumber % 1 === 0 ? valueNumber : valueNumber.toFixed(2);
  const infoDisplay =
    item.structure === '% Discount'
      ? `${valueDisplay}%`
      : ChiroUpBaseCommon.format.asMoney(item.value);

  switch (instanceKey) {
    case 'payors':
      return item.payorID
        ? `${item.legalName}${item.internalName ? ` (${item.internalName})` : ''} - ${item.payorID}`
        : `${item.legalName}${item.internalName ? ` (${item.internalName})` : ''}`;
    case 'billing-codes':
      return `${item.codeSet} - ${item.code} - ${item.description}`;
    case 'billing-adjustments':
      return `${item.name} - ${item.structure} - ${infoDisplay}`;
    default:
      return '';
  }
};

/**
 * 13052385647: errors for a code's additional billed amount rows, keyed like the form fields.
 * A row's shortcut can't repeat another row's or the code's own (its primary amount's), and
 * the code needs its own shortcut once it has rows. The save rules use it, and the editor shows the shortcut errors while the user types.
 */
export const getBilledAmountRowErrors = (
  billedAmounts: BillingCodeBilledAmount[],
  codeShortcut?: string | null
) => {
  const fieldErrors: FormErrorsStrict['fieldErrors'] = {};
  const primaryShortcut = codeShortcut?.trim();
  const shortcutCounts = billedAmounts.reduce(
    (a, row) => {
      const shortcut = row.shortcut?.trim();

      if (shortcut) {
        a[shortcut] = (a[shortcut] ?? 0) + 1;
      }

      return a;
    },
    (primaryShortcut ? { [primaryShortcut]: 1 } : {}) as Record<string, number>
  );

  if (!primaryShortcut) {
    fieldErrors.codeShortcut = {
      message: 'Add a shortcut so staff can tell these billed amounts apart.',
    };
  }

  billedAmounts.forEach((row, index) => {
    const shortcut = row.shortcut?.trim();

    if (!row.billedAmount) {
      fieldErrors[`billedAmounts[${index}].billedAmount`] = { message: 'Required.' };
    } else if (!Number.isFinite(Number(row.billedAmount))) {
      fieldErrors[`billedAmounts[${index}].billedAmount`] = {
        message: 'Billed amount must be a number.',
      };
    }

    if (!shortcut) {
      fieldErrors[`billedAmounts[${index}].shortcut`] = {
        message: 'Add a shortcut so staff can tell these billed amounts apart.',
      };
    } else if (!/^[0-9a-zA-Z._-]+$/.test(shortcut)) {
      // Same characters TidyInputValue.BillingCodeShortcut keeps on the form.
      fieldErrors[`billedAmounts[${index}].shortcut`] = {
        message: 'Shortcuts can only use letters, numbers, periods, underscores and hyphens.',
      };
    } else if (shortcutCounts[shortcut] > 1) {
      fieldErrors[`billedAmounts[${index}].shortcut`] = {
        message: 'Each billed amount on this code needs a different shortcut.',
      };
    }
  });

  return fieldErrors;
};

export const applyDatabaseSaveRules = ({
  instanceKey,
  item,
  clinicId,
}: {
  instanceKey: InstanceKeys | string;
  item: any;
  clinicId: number | undefined;
}) => {
  // Sanity checks we want on the front and backend.
  const errors: FormErrorsStrict = {
    form: [],
    fieldErrors: {},
  };

  if (!clinicId) {
    errors.form.push({ message: 'Clinic ID is required.' });
  }

  switch (instanceKey) {
    // Billing codes returns something in the form that useForm does on
    // the frontend. Was thinking of using the normal BusinessRuleItemType,
    // with a translator. For later: TODO.
    case 'billingCodes':
    case InstanceKeys.billingCodes: {
      const typedItem = item as BillingCode;
      const message = 'Required.';

      if (!typedItem.code) {
        errors.fieldErrors.code = { message };
      }

      if (!typedItem.description && typedItem.codeSet !== CodeSets.NDC) {
        errors.fieldErrors.description = {
          message,
        };
      }

      if (!typedItem.codeSet) {
        errors.fieldErrors.codeSet = { message };
      }

      if (typedItem.codeSet === CodeSets.NDC) {
        if (!typedItem.unitType) {
          errors.fieldErrors.unitType = { message };
        }
      }

      if (typedItem.codeSet === CodeSets.SERVICE || typedItem.codeSet === CodeSets.CPT) {
        if (!typedItem.billedAmount) {
          errors.fieldErrors.billedAmount = {
            message,
          };
        }
      }

      // 13052385647: the code's own billed amount and shortcut are its primary; billedAmounts
      // are additional amounts, each with its own shortcut.
      const billedAmounts = typedItem.billedAmounts ?? [];

      if (billedAmounts.length) {
        if (typedItem.codeSet !== CodeSets.SERVICE && typedItem.codeSet !== CodeSets.CPT) {
          errors.fieldErrors.billedAmounts = {
            message: 'Only CPT and Service codes can have more than one billed amount.',
          };
        } else {
          Object.assign(
            errors.fieldErrors,
            getBilledAmountRowErrors(billedAmounts, typedItem.codeShortcut)
          );
        }
      }

      // A payor's allowed amount is checked against the code's highest billed amount, its own
      // included; invalid rows are already flagged above.
      const highestBilledAmount = Math.max(
        Number(typedItem.billedAmount),
        ...billedAmounts
          .filter(row => row.billedAmount)
          .map(row => Number(row.billedAmount))
          .filter(Number.isFinite)
      );

      const twoCharModifiers = 'Modifiers must be two characters.',
        defaultModifiersByValue = {} as STRING_BOOLEAN_HASH,
        res =
          typedItem.defaultModifiers?.reduce((a, mod) => {
            if (!defaultModifiersByValue[mod]) {
              defaultModifiersByValue[mod] = true;
              a.push(mod);
            }

            return a;
          }, [] as string[]) || []; // Remove dupes but keep order.
      typedItem.defaultModifiers = res ?? [];

      typedItem.defaultModifiers?.forEach((mod, index) => {
        if (mod) {
          if (mod.length !== 2) {
            errors.fieldErrors[`defaultModifiers[${index}]`] = {
              message: twoCharModifiers,
            };
          }
        }
      });

      if (typedItem.payors && Array.isArray(typedItem.payors) && typedItem.payors.length) {
        typedItem.payors.forEach((payor, index) => {
          if (!payor.payorID) {
            errors.form.push({
              message: `Payor # ${index + 1} needs an id.`,
            });
          }

          // if (!payor.allowedAmount) {
          //   errors.fieldErrors[`payor[${index}].allowedAmount`] = {
          //     message: 'Allowed amount is required.',
          //   };
          // }
          if (Number(payor.allowedAmount) > highestBilledAmount) {
            errors.fieldErrors[`payor[${index}].allowedAmount`] = {
              message: 'Allowed amount cannot be more than billed amount.',
            };
          }

          let oops = [] as string[];

          const usedPayorModifiers = {} as STRING_BOOLEAN_HASH,
            modifierKeys = ['modifier1', 'modifier2', 'modifier3', 'modifier4'],
            usedModifierValues = [] as string[];
          modifierKeys.forEach(mod => {
            const anyPayor = payor as any;
            if (!anyPayor[mod]) return;

            if (!usedPayorModifiers[anyPayor[mod]]) {
              usedPayorModifiers[anyPayor[mod]] = true;
              usedModifierValues.push(anyPayor[mod]);
            }

            // 5-5-25 per #9048909386 we do not care about this for now
            // if (defaultModifiersByValue[anyPayor[mod] ?? '']) {
            //   oops.push('Duplicates a default modifier.');
            // }

            if (anyPayor[mod] && anyPayor[mod].length !== 2) {
              oops.push(twoCharModifiers);
            }

            if (oops.length) {
              errors.fieldErrors[`payor[${index}].${mod}`] = {
                message: oops.join(' '),
              };
            }

            oops = [];
          });

          modifierKeys.forEach((mod, i) => {
            const anyPayor = payor as any;

            if (usedModifierValues[i]) {
              anyPayor[mod] = usedModifierValues[i];
            } else {
              delete anyPayor[mod];
            }
          });
        });
      }

      const fieldErrorKeys = Object.keys(errors.fieldErrors);

      if (fieldErrorKeys.length) {
        errors.form.push({ message: 'There are field errors.' });
      }

      break;
    }
    default:
      break;
  }

  return errors;
};

/**
 * This is in terms of the string that the user sees. It could change, so
 * having two methods for future expansion.
 *
 * @param instanceKey
 * @param item
 * @returns
 */
export const itemToDataDescription = (instanceKey: string, item: any) => {
  return itemToOptionText(instanceKey, item);
};
